import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Filter } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { useLanguage } from "@/shared/hooks/use-language";
import { useNumberFormat } from "@/shared/hooks/use-number-format";
import { formatCount } from "@/shared/lib/amount";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";
import { exitToLogin } from "@/shared/lib/session-exit";
import { calendarDayInFilterZone } from "@/shared/lib/date-utils";
import { labelOr } from "@/shared/lib/label";
import type { ScopeContext } from "@/features/auth";
import { getInvestors, type Investor } from "@/features/auth/api/investor";
import { accountScopeOf, getPortfolios, type Portfolio } from "@/features/portfolio/api/portfolio";
import {
  getExternalTradeEvents,
  getExternalTrades,
  type ExternalTrade,
} from "@/features/portfolio/api/external-trade";
import { TransactionCard } from "./components/TransactionCard";
import type { LogsState } from "./components/TransactionLogs";
import { TransactionFilterSheet } from "./components/TransactionFilterSheet";
import {
  DEFAULT_TRANSACTION_FILTERS,
  countActiveFilters,
  type TransactionFilters,
} from "./components/transaction-filters";

// One request per owned portfolio, so this bounds the requests a single page load may
// issue. Portfolios beyond it are reported as omitted rather than silently dropped.
const MAX_PORTFOLIO_REQUESTS_PER_LOAD = 25;

type IdentityStatus = "loading" | "resolved" | "notFound" | "unavailable";
type LoadStatus = "loading" | "loaded" | "failed";

const InvestorDetail = () => {
  const { t } = useLanguage();
  const { numberFormat } = useNumberFormat();
  const navigate = useNavigate();
  const { investorId } = useParams<{ investorId: string }>();
  const sessionId = ScopeContextStorage.getCurrentSessionId();
  const scopeContext: ScopeContext | null = sessionId
    ? ScopeContextStorage.getScopeContext(sessionId)
    : null;
  const accountScope = scopeContext ? accountScopeOf(scopeContext) : null;

  const [investor, setInvestor] = useState<Investor | null>(null);
  const [identityStatus, setIdentityStatus] = useState<IdentityStatus>("loading");
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [portfoliosStatus, setPortfoliosStatus] = useState<LoadStatus>("loading");
  const [trades, setTrades] = useState<ExternalTrade[]>([]);
  const [tradesStatus, setTradesStatus] = useState<LoadStatus>("loading");
  const [failedPortfolioCount, setFailedPortfolioCount] = useState(0);
  const [queriedPortfolioCount, setQueriedPortfolioCount] = useState(0);
  const [omittedPortfolioCount, setOmittedPortfolioCount] = useState(0);
  const [morePortfoliosExist, setMorePortfoliosExist] = useState(false);
  const [tradesTruncated, setTradesTruncated] = useState(false);
  const [scopeMissing, setScopeMissing] = useState(false);

  const [filters, setFilters] = useState<TransactionFilters>(DEFAULT_TRANSACTION_FILTERS);
  const [draftFilters, setDraftFilters] = useState<TransactionFilters>(DEFAULT_TRANSACTION_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Bumped by `clearFilters`, and folded into `TransactionFilterSheet`'s two `DateField`
  // `key`s. `draftFilters` alone cannot force an unparsed entry off the screen when the
  // clear sets it to the same `""` the field already reported — see `DateField`'s own doc
  // comment and `access/index.tsx`'s `historyResetCount`, the sibling of this counter.
  const [filterResetCount, setFilterResetCount] = useState(0);

  const [expandedTradeIds, setExpandedTradeIds] = useState<string[]>([]);
  const [logsByTradeId, setLogsByTradeId] = useState<Record<string, LogsState>>({});

  const refreshSequence = useRef(0);

  const refresh = useCallback(() => {
    if (!sessionId || !accountScope || !investorId) {
      setScopeMissing(true);
      return;
    }
    setScopeMissing(false);

    const sequence = refreshSequence.current + 1;
    refreshSequence.current = sequence;
    setIdentityStatus("loading");
    setPortfoliosStatus("loading");
    setTradesStatus("loading");
    setFailedPortfolioCount(0);
    setOmittedPortfolioCount(0);
    setMorePortfoliosExist(false);
    setTradesTruncated(false);
    setExpandedTradeIds([]);
    setLogsByTradeId({});

    Promise.allSettled([
      getInvestors(sessionId, accountScope),
      getPortfolios(sessionId, accountScope, { ownerId: investorId }),
    ]).then(([investorResult, portfolioResult]) => {
      if (refreshSequence.current !== sequence) return;

      if (investorResult.status === "rejected") {
        setInvestor(null);
        setIdentityStatus("unavailable");
      } else {
        const list = investorResult.value.investors || [];
        const match = list.find((entry) => entry.id === investorId) || null;
        setInvestor(match);
        if (match) {
          setIdentityStatus("resolved");
        } else {
          // A truncated list has not earned the claim that the investor does not exist.
          setIdentityStatus(investorResult.value.cursor ? "unavailable" : "notFound");
        }
      }

      if (portfolioResult.status === "rejected") {
        setPortfolios([]);
        setPortfoliosStatus("failed");
        setTradesStatus("failed");
        return;
      }

      const owned = portfolioResult.value.portfolios || [];
      setPortfolios(owned);
      setPortfoliosStatus("loaded");

      const queried = owned.slice(0, MAX_PORTFOLIO_REQUESTS_PER_LOAD);
      setQueriedPortfolioCount(queried.length);
      // Whole portfolios left unread — distinct from a trade page trimmed by recency,
      // because every one of their transactions is missing, not just the older ones. A
      // cursor means "at least one more" without saying how many, so it sets the flag
      // rather than inflating the count.
      setOmittedPortfolioCount(owned.length - queried.length);
      setMorePortfoliosExist(Boolean(portfolioResult.value.cursor));

      if (queried.length === 0) {
        setTrades([]);
        setTradesStatus("loaded");
        return;
      }

      Promise.allSettled(
        queried.map((portfolio) =>
          // No status filter, deliberately: this is a transaction history rather than a
          // working blotter, so a cancelation belongs in it even though the portfolio
          // blotter hides one. The ordering is stated rather than inherited, so the page
          // the 100-row cap trims does not move when the backend default changes.
          getExternalTrades(sessionId, accountScope, {
            portfolioId: portfolio.id,
            orderBy: "-reference_time",
          }),
        ),
      ).then((tradeResults) => {
        if (refreshSequence.current !== sequence) return;

        const failed = tradeResults.filter((result) => result.status === "rejected").length;
        setFailedPortfolioCount(failed);

        if (failed === tradeResults.length) {
          setTrades([]);
          setTradesStatus("failed");
          return;
        }

        const collected: ExternalTrade[] = [];
        let pageTruncated = false;
        for (const result of tradeResults) {
          if (result.status !== "fulfilled") continue;
          collected.push(...(result.value.trades || []));
          pageTruncated = pageTruncated || Boolean(result.value.cursor);
        }
        collected.sort((a, b) => {
          const byReference = (b.reference_time || "").localeCompare(a.reference_time || "");
          if (byReference !== 0) return byReference;
          return (b.created || "").localeCompare(a.created || "");
        });
        setTrades(collected);
        setTradesTruncated(pageTruncated);
        setTradesStatus("loaded");
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const portfolioNameById = useMemo(() => {
    const names: Record<string, string> = {};
    for (const portfolio of portfolios) {
      names[portfolio.id] = portfolio.name;
    }
    return names;
  }, [portfolios]);

  const visibleTrades = useMemo(() => {
    return trades.filter((trade) => {
      if (filters.sides.length > 0 && !filters.sides.includes(trade.side)) return false;
      if (filters.statuses.length > 0) {
        const matches = filters.statuses.some((status) => status === trade.status);
        if (!matches) return false;
      }
      // Filter bounds are bare calendar days; `reference_time` is an instant. Comparing the
      // instant string directly against the bound excludes a trade on the end day itself —
      // `"...T13:05:00+00:00" > "2026-08-20"` is true under a plain string compare, since
      // the longer string is a superstring of the bound. Compare calendar days instead.
      const referenceDay = calendarDayInFilterZone(trade.reference_time) || "";
      if (filters.startDate && referenceDay < filters.startDate) return false;
      if (filters.endDate && referenceDay > filters.endDate) return false;
      return true;
    });
  }, [trades, filters]);

  const activeFilterCount = countActiveFilters(filters);

  // Whether anything the investor owns went unread — a portfolio that failed, one the cap
  // never queried, or a trade page the cursor trimmed. An empty list is only "no
  // transactions" when none of these hold; otherwise the page is describing what it
  // managed to read, not the investor's history.
  const historyIncomplete =
    failedPortfolioCount > 0 || omittedPortfolioCount > 0 || morePortfoliosExist;

  const loadLogs = useCallback(
    (tradeId: string) => {
      if (!sessionId || !accountScope) return;
      const sequence = refreshSequence.current;
      setLogsByTradeId((previous) => ({
        ...previous,
        [tradeId]: { status: "loading", events: [] },
      }));
      getExternalTradeEvents(sessionId, accountScope, { tradeIds: [tradeId] })
        .then((response) => {
          if (refreshSequence.current !== sequence) return;
          setLogsByTradeId((previous) => ({
            ...previous,
            [tradeId]: { status: "loaded", events: response.events || [] },
          }));
        })
        .catch(() => {
          if (refreshSequence.current !== sequence) return;
          setLogsByTradeId((previous) => ({
            ...previous,
            [tradeId]: { status: "failed", events: [] },
          }));
        });
    },
    [sessionId, accountScope],
  );

  const toggleTrade = (tradeId: string) => {
    // The load decision is taken here, not inside the updater: an updater must be pure,
    // and StrictMode double-invokes it, which fired two requests per expand.
    const isExpanded = expandedTradeIds.includes(tradeId);
    if (!isExpanded && !logsByTradeId[tradeId]) {
      loadLogs(tradeId);
    }
    setExpandedTradeIds((previous) =>
      previous.includes(tradeId)
        ? previous.filter((entry) => entry !== tradeId)
        : [...previous, tradeId],
    );
  };

  const openFilters = () => {
    setDraftFilters(filters);
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setDraftFilters(DEFAULT_TRANSACTION_FILTERS);
    setFilters(DEFAULT_TRANSACTION_FILTERS);
    setFiltersOpen(false);
    setFilterResetCount((count) => count + 1);
  };

  const isLoading = portfoliosStatus === "loading" || tradesStatus === "loading";
  const showList =
    identityStatus !== "notFound" && portfoliosStatus === "loaded" && portfolios.length > 0;

  return (
    <main className="flex-1 p-4 md:p-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("investorDetail.back")}
            onClick={() => navigate("/console/investors")}
            data-testid="investor-detail-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-foreground mb-1">
              {t("investorDetail.title")}
            </h1>
            <p className="text-muted-foreground">{t("investorDetail.subtitle")}</p>
          </div>
          {showList && (
            <Button variant="outline" className="gap-2" onClick={openFilters} data-testid="investor-filters">
              <Filter className="w-4 h-4" />
              {t("investorDetail.filters")}
              {activeFilterCount > 0 && (
                <Badge variant="default" className="ml-1 h-5 px-1.5 text-xs" data-testid="investor-filter-count">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          )}
        </div>

        {scopeMissing && (
          <Card className="p-6 border-destructive" data-testid="investor-no-scope">
            <p className="text-destructive mb-4">{t("investorDetail.noScope")}</p>
            <Button variant="outline" onClick={exitToLogin}>
              {t("investorDetail.signInAgain")}
            </Button>
          </Card>
        )}

        {!scopeMissing && (
        <Card className="bg-card border-border p-6 mb-6" data-testid="investor-identity">
          {identityStatus === "loading" && (
            <div className="space-y-2">
              <div className="skeleton-shimmer h-6 w-48 rounded bg-muted" />
              <div className="skeleton-shimmer h-4 w-32 rounded bg-muted" />
            </div>
          )}
          {identityStatus === "resolved" && investor && (
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-semibold text-primary">
                {investor.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-foreground">{investor.name}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("investors.taxId")}: {investor.tax_id}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline">
                  {labelOr(t, `investors.${investor.status}`, investor.status)}
                </Badge>
                {portfoliosStatus === "loaded" && (
                  <span className="text-sm text-muted-foreground" data-testid="investor-portfolio-count">
                    {t("investorDetail.portfolioCount").replace("{n}", formatCount(portfolios.length, numberFormat))}
                  </span>
                )}
              </div>
            </div>
          )}
          {identityStatus === "unavailable" && (
            <div>
              <p className="text-muted-foreground" data-testid="investor-identity-unavailable">
                {t("investorDetail.identityUnavailable")}
              </p>
              <p className="font-mono text-xs text-muted-foreground mt-1">{investorId}</p>
            </div>
          )}
          {identityStatus === "notFound" && (
            <p className="text-muted-foreground" data-testid="investor-not-found">
              {t("investorDetail.notFound")}
            </p>
          )}
        </Card>
        )}

        {portfoliosStatus === "failed" && (
          <Card className="p-6 border-destructive" data-testid="investor-portfolios-failed">
            <p className="text-destructive mb-4">{t("investorDetail.portfoliosLoadFailed")}</p>
            <Button variant="outline" onClick={refresh} data-testid="investor-retry">
              {t("investorDetail.retry")}
            </Button>
          </Card>
        )}

        {identityStatus !== "notFound" && portfoliosStatus === "loaded" && portfolios.length === 0 && (
          <Card className="bg-card border-border p-10 text-center" data-testid="investor-no-portfolio">
            <p className="text-foreground font-medium mb-1">{t("investorDetail.noPortfolio")}</p>
            <p className="text-muted-foreground mb-6">{t("investorDetail.noPortfolioHint")}</p>
            <Button onClick={() => navigate("/console/portfolio")}>
              {t("investorDetail.goToPortfolios")}
            </Button>
          </Card>
        )}

        {showList && (
          <>
            {isLoading && (
              <div className="space-y-4">
                <Card className="p-6">
                  <div className="skeleton-shimmer h-4 w-1/2 rounded bg-muted mb-3" />
                  <div className="skeleton-shimmer h-4 w-1/3 rounded bg-muted" />
                </Card>
                <Card className="p-6">
                  <div className="skeleton-shimmer h-4 w-2/3 rounded bg-muted mb-3" />
                  <div className="skeleton-shimmer h-4 w-1/4 rounded bg-muted" />
                </Card>
              </div>
            )}

            {!isLoading && tradesStatus === "failed" && (
              <Card className="p-6 border-destructive" data-testid="investor-load-failed">
                <p className="text-destructive mb-4">{t("investorDetail.loadFailed")}</p>
                <Button variant="outline" onClick={refresh} data-testid="investor-retry">
                  {t("investorDetail.retry")}
                </Button>
              </Card>
            )}

            {!isLoading && tradesStatus === "loaded" && (
              <div className="space-y-4">
                {failedPortfolioCount > 0 && (
                  <Card
                    className="p-4 border-destructive/40 bg-destructive/5 text-sm text-destructive"
                    data-testid="investor-partial-failure"
                  >
                    <p className="mb-3">
                      {t("investorDetail.partialFailure")
                        .replace("{n}", formatCount(failedPortfolioCount, numberFormat))
                        .replace("{total}", formatCount(queriedPortfolioCount, numberFormat))}
                    </p>
                    <Button variant="outline" size="sm" onClick={refresh} data-testid="investor-retry">
                      {t("investorDetail.retry")}
                    </Button>
                  </Card>
                )}

                {/* An empty result only means "no transactions" when every source answered;
                    otherwise the page has not read enough to make that claim. */}
                {trades.length === 0 && !historyIncomplete && (
                  <Card className="bg-card border-border p-10 text-center text-muted-foreground" data-testid="investor-no-transactions">
                    {t("investorDetail.noTransactions")}
                  </Card>
                )}

                {trades.length === 0 && historyIncomplete && (
                  <Card
                    className="bg-card border-border p-10 text-center text-muted-foreground"
                    data-testid="investor-no-transactions-partial"
                  >
                    {t("investorDetail.noTransactionsPartial")}
                  </Card>
                )}

                {trades.length > 0 && visibleTrades.length === 0 && (
                  <Card className="bg-card border-border p-10 text-center" data-testid="investor-no-matches">
                    <p className="text-muted-foreground mb-6">
                      {historyIncomplete || tradesTruncated
                        ? t("investorDetail.noMatchesPartial")
                        : t("investorDetail.noMatches")}
                    </p>
                    <Button variant="outline" onClick={clearFilters} data-testid="investor-clear-from-empty">
                      {t("investorDetail.clearFilters")}
                    </Button>
                  </Card>
                )}

                {visibleTrades.map((trade) => (
                  <TransactionCard
                    key={trade.id}
                    trade={trade}
                    portfolioName={portfolioNameById[trade.portfolio_id] ?? null}
                    showPortfolio={portfolios.length > 1}
                    isExpanded={expandedTradeIds.includes(trade.id)}
                    logs={logsByTradeId[trade.id]}
                    onToggle={() => toggleTrade(trade.id)}
                    onRetryLogs={() => loadLogs(trade.id)}
                  />
                ))}

                {tradesTruncated && (
                  <p className="text-sm text-muted-foreground" data-testid="investor-truncated">
                    {t("investorDetail.truncated")}
                  </p>
                )}
              </div>
            )}

            {/* Omitted portfolios are missing in full, not trimmed by recency, so this
                says so separately — and outside the loaded branch, since a fan-out that
                then fails entirely was still capped. */}
            {!isLoading && (omittedPortfolioCount > 0 || morePortfoliosExist) && (
              <p className="text-sm text-muted-foreground mt-4" data-testid="investor-portfolios-omitted">
                {omittedPortfolioCount > 0
                  ? t("investorDetail.portfoliosOmitted")
                      .replace("{n}", formatCount(omittedPortfolioCount, numberFormat))
                      .replace("{queried}", formatCount(queriedPortfolioCount, numberFormat))
                  : t("investorDetail.morePortfoliosExist")}
              </p>
            )}
          </>
        )}
      </div>

      <TransactionFilterSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        draft={draftFilters}
        onDraftChange={setDraftFilters}
        onApply={applyFilters}
        onClear={clearFilters}
        resetCount={filterResetCount}
      />
    </main>
  );
};

export default InvestorDetail;
